import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";
import DrawerFilter from "../../../components/listing/DrawerFilter";

const PAGE_SIZE = 20;

// --- HELPERS INTERNOS (Se mantienen igual) ---
function useDebounced(v, delay = 300) {
  const [val, setVal] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setVal(v), delay);
    return () => clearTimeout(t);
  }, [v, delay]);
  return val;
}

const isAbort = (e) =>
  e?.name === "AbortError" || /aborted|abort/i.test(e?.message || "");

export default function FiltroParametros({
  initialFilters = {
    nombre: "",
    valor: "",
    fecha: "",
    numero: "",
    prioridad: "",
  },
  onSubmit = () => {},
  onClose = () => {},
}) {
  // ===== LOGICA AUTOCOMPLETE NOMBRE (Mantenida intacta) =====
  const [nombreInput, setNombreInput] = useState(initialFilters.nombre || "");
  const debNombre = useDebounced(nombreInput, 300);
  const [nombreItems, setNombreItems] = useState([]);
  const [nombreOpen, setNombreOpen] = useState(false);
  const [nombreLoading, setNombreLoading] = useState(false);
  const [nombreHasMore, setNombreHasMore] = useState(false);
  const [nombrePage, setNombrePage] = useState(0);
  const nombreBoxRef = useRef(null);
  const nombreAbort = useRef(null);

  // ===== OTROS ESTADOS =====
  const [valor, setValor] = useState(initialFilters.valor || "");
  const [fecha, setFecha] = useState(initialFilters.fecha || "");
  const [numero, setNumero] = useState(initialFilters.numero || "");
  const [prioridad, setPrioridad] = useState(initialFilters.prioridad || "");

  // Estado para el botón de "Aplicar" del Drawer
  const [isLoading, setIsLoading] = useState(false);

  // Rehidratar valores
  useEffect(() => {
    setNombreInput(initialFilters.nombre || "");
    setValor(initialFilters.valor || "");
    setFecha(initialFilters.fecha || "");
    setNumero(initialFilters.numero || "");
    setPrioridad(initialFilters.prioridad || "");
  }, [initialFilters]);

  // --- Lógica de Fetch Nombre ---
  const cancelNombre = () => {
    try {
      nombreAbort.current?.abort();
    } catch {}
  };

  const fetchNombre = async (q, page, signal) => {
    const url = apiRoutes.parametros.autocompleteUrl({
      q,
      page,
      size: PAGE_SIZE,
    });
    const data = await apiFetchJson(url, { signal });
    return {
      list: data.items || [],
      hasMore: Boolean(data.hasMore),
      page: data.page ?? page,
    };
  };

  useEffect(() => {
    if (!nombreOpen) return;
    cancelNombre();
    const ctrl = new AbortController();
    nombreAbort.current = ctrl;
    setNombreLoading(true);

    (async () => {
      try {
        const { list, hasMore, page } = await fetchNombre(
          debNombre,
          0,
          ctrl.signal,
        );
        setNombreItems(list);
        setNombreHasMore(hasMore);
        setNombrePage(page);
      } catch (e) {
        if (!isAbort(e)) console.error("Autocomplete parámetro:", e);
      } finally {
        setNombreLoading(false);
      }
    })();
    return cancelNombre;
  }, [nombreOpen, debNombre]);

  const onNombreScroll = (e) => {
    const el = e.currentTarget;
    if (!nombreHasMore || nombreLoading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      const next = nombrePage + 1;
      const ctrl = new AbortController();
      nombreAbort.current = ctrl;
      setNombreLoading(true);

      fetchNombre(debNombre, next, ctrl.signal)
        .then(({ list, hasMore, page }) => {
          setNombreItems((prev) => [...prev, ...list]);
          setNombreHasMore(hasMore);
          setNombrePage(page);
        })
        .finally(() => setNombreLoading(false));
    }
  };

  // Clic fuera para cerrar dropdown
  useEffect(() => {
    const onDoc = (e) => {
      if (nombreBoxRef.current && !nombreBoxRef.current.contains(e.target)) {
        setNombreOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // ===== SUBMIT MANEJADO POR DRAWER =====
  const handleApply = () => {
    setIsLoading(true);
    
    onSubmit({
      nombre: nombreInput.trim(),
      valor: valor.trim(),
      fecha: fecha.trim(),
      numero: numero.trim(),
      prioridad: prioridad.trim(),
    });

    setIsLoading(false);
    onClose();
  };

  return (
    <DrawerFilter
      title="Filtros de Parámetros"
      onClose={onClose}
      onApply={handleApply}
      isLoading={isLoading}
    >
      {/* 1. AUTOCOMPLETE MANUAL (NOMBRE) */}
      <div ref={nombreBoxRef} className="relative">
        <label className="form-label-fil">Nombre del parámetro:</label>
        <input
          type="text"
          className="input mt-1 w-full"
          placeholder="Buscar parámetro..."
          value={nombreInput}
          onChange={(e) => {
            setNombreInput(e.target.value);
            setNombreOpen(true);
          }}
          onFocus={() => setNombreOpen(true)}
        />

        {nombreOpen && (
          <div
            className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-auto text-sm"
            onScroll={onNombreScroll}
          >
            {nombreItems.map((p) => (
              <button
                key={p.id}
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-gray-50 last:border-0"
                onClick={() => {
                  setNombreInput(p.nombre);
                  setNombreOpen(false);
                }}
              >
                <div className="text-xs text-gray-500">{p.nombre}</div>
                <div className="text-sm font-medium">{p.valor ?? ""}</div>
              </button>
            ))}

            {nombreLoading && (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                Cargando...
              </div>
            )}

            {!nombreLoading && nombreItems.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                Sin resultados
              </div>
            )}
          </div>
        )}
      </div>

      {/* 2. VALOR */}
      <div>
        <label className="form-label-fil">Valor:</label>
        <input
          type="text"
          className="input mt-1 w-full"
          placeholder="Texto del valor..."
          value={valor}
          onChange={(e) => setValor(e.target.value)}
        />
      </div>

    {/* 3. FECHA */}
      <div>
        <label className="form-label-fil">Fecha:</label>
        <input
          type="date"
          className="input mt-1 w-full"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
        />
      </div>

      {/* 4. NÚMERO */}
      <div>
        <label className="form-label-fil">Número:</label>
        <input
          type="text"
          className="input mt-1 w-full"
          placeholder="Ej.: 8401477"
          value={numero}
          onChange={(e) => setNumero(e.target.value)}
        />
      </div>

      {/* 5. PRIORIDAD */}
      <div>
        <label className="form-label-fil">Prioridad:</label>
        <input
          type="text"
          className="input mt-1 w-full"
          placeholder="Prioridad..."
          value={prioridad}
          onChange={(e) => setPrioridad(e.target.value)}
        />
      </div>

    </DrawerFilter>
  );
}